import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import heroBackground from "@/assets/hero-background.jpg";
import { 
  Award, 
  Globe, 
  Users, 
  Building, 
  CheckCircle,
  ArrowRight,
  Target,
  Eye,
  Heart,
  Facebook,
  Linkedin
} from "lucide-react";

const About = () => {
  const milestones = [
    { year: "1995", event: "Company Founded", description: "Started as a small fabrication workshop" },
    { year: "2005", event: "First Major Contract", description: "Secured pharmaceutical industry partnership" },
    { year: "2010", event: "EP Facility Launch", description: "Southeast Asia's largest electropolishing facility" },
    { year: "2015", event: "International Expansion", description: "Serving clients across ASEAN region" },
    { year: "2020", event: "Technology Upgrade", description: "Advanced polishing and testing equipment" },
    { year: "2024", event: "Industry Leadership", description: "Malaysia's premier tank fabricator" }
  ];

  const values = [
    {
      icon: <Award className="h-8 w-8 text-wle-red" />,
      title: "Quality Excellence",
      description: "Every tank meets the highest industry standards with mirror-finish surface quality"
    },
    {
      icon: <Users className="h-8 w-8 text-wle-blue" />,
      title: "Customer Focus",
      description: "Custom solutions tailored to each client's specific requirements and applications"
    },
    {
      icon: <Target className="h-8 w-8 text-wle-red" />,
      title: "Precision Engineering",
      description: "Advanced manufacturing processes with MITUTOYO certified surface testing"
    },
    {
      icon: <Globe className="h-8 w-8 text-wle-blue" />,
      title: "Global Standards",
      description: "Serving local and international markets with world-class fabrication capabilities"
    }
  ];

  const stats = [
    { number: "1000+", label: "Tanks Delivered", color: "text-wle-red" },
    { number: "25+", label: "Years Experience", color: "text-wle-blue" },
    { number: "100+", label: "Happy Clients", color: "text-wle-red" },
    { number: "5", label: "Industry Sectors", color: "text-wle-blue" }
  ];

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>About WLE Technology | 25+ Years Tank Fabrication Excellence</title>
        <meta name="description" content="Learn about WLE Technology's 25+ years of excellence in tank fabrication, electropolishing, and mechanical solutions. Serving pharmaceutical, F&B, and chemical industries across Malaysia." />
        <link rel="canonical" href="https://wletec.com/about" />
        <meta property="og:title" content="About WLE Technology - Malaysia Tank Fabrication Expert" />
        <meta property="og:description" content="25+ years of engineering excellence in custom tank fabrication and surface finishing solutions." />
        <meta property="og:url" content="https://wletec.com/about" />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="WLE Technology, tank fabrication company, Malaysia engineering, stainless steel fabrication" />
      </Helmet>
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="py-20 relative"
        style={{
          backgroundImage: `url(${heroBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/90 to-background/80" />
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-6">
              About WLE
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Malaysia Mechanical and{" "}
              <span className="bg-gradient-primary bg-clip-text text-transparent">
                System Solutions
              </span>
            </h1>
            
            <div className="flex flex-wrap gap-2 mb-6 justify-center">
              <Badge variant="secondary" className="bg-wle-red text-white border-wle-red">Tank Fabricator</Badge>
              <Badge variant="secondary" className="bg-wle-blue text-white border-wle-blue">Mechanical and Electro Polish Specialist</Badge>
            </div>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Malaysia's premier mechanical and system solutions provider, delivering comprehensive 
              integrated solutions from concept to commissioning. We specialize in custom tank 
              fabrication, precision mechanical polishing, advanced electropolishing, and complete 
              turnkey projects with decades of engineering excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16">
        <div className="container">
          <div className="grid md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className={`text-4xl lg:text-5xl font-bold mb-2 ${stat.color}`}>
                  {stat.number}
                </div>
                <div className="text-muted-foreground font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-subtle-texture">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold">Our Story</h2>
              <p className="text-lg text-muted-foreground">
                Founded as Malaysia's comprehensive mechanical and system solutions provider, 
                WLE has evolved to become the nation's leading expert in integrated mechanical 
                systems, tank fabrication, and surface finishing technologies.
              </p>
              <p className="text-muted-foreground">
                Our mechanical and system solutions encompass complete project lifecycle management - 
                from initial engineering design and procurement to construction, installation, and 
                commissioning. We deliver turnkey mechanical systems that integrate seamlessly with 
                our tank fabrication, mechanical polishing, and electropolishing expertise.
              </p>
              <p className="text-muted-foreground">
                As a mechanical and system solutions provider, we offer comprehensive engineering 
                services, custom mechanical equipment design, process piping systems, and complete 
                facility integration - ensuring every component works harmoniously for optimal performance.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-wle-blue" />
                  <span>Comprehensive mechanical and system solutions</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-wle-red" />
                  <span>Tank fabrication with 100% in-house finishing</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-wle-blue" />
                  <span>Mechanical and electropolishing specialist</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-wle-red" />
                  <span>Complete turnkey project capabilities</span>
                </div>
              </div>

              <Button asChild>
                <Link to="/services">
                  Explore Our Services
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>

            <div className="bg-card p-8 rounded-lg border shadow-elegant">
              <h3 className="text-2xl font-bold mb-6">Company Milestones</h3>
              <div className="space-y-6">
                {milestones.map((milestone, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold">
                        {milestone.year}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg">{milestone.event}</h4>
                      <p className="text-muted-foreground text-sm">{milestone.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Our Core Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do, from initial design 
              to final delivery and beyond
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-0 shadow-elegant hover:shadow-blue transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    {value.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-3">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            <Card className="border-0 shadow-elegant">
              <CardContent className="p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-wle-red flex items-center justify-center">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">Our Mission</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  To be Malaysia's leading mechanical and system solutions provider, delivering 
                  comprehensive tank fabrication, mechanical polishing, and electropolishing 
                  services. We're committed to providing complete turnkey projects with 
                  unmatched quality, precision, and engineering excellence.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-elegant">
              <CardContent className="p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-wle-blue flex items-center justify-center">
                    <Eye className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">Our Vision</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  To become Southeast Asia's premier mechanical and system solutions provider, 
                  recognized for innovation in tank fabrication, mechanical polishing, and 
                  electropolishing. We envision a future where industries rely on WLE's 
                  integrated mechanical solutions for their critical processes.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-grid-pattern">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Expert Team</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our experienced engineers, fabricators, and quality specialists work together 
              to deliver exceptional results on every project
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-elegant">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-wle-red flex items-center justify-center mx-auto mb-4">
                  <Building className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Engineering Team</h3>
                <p className="text-sm text-muted-foreground">
                  Experienced engineers specializing in custom tank design and EPCC project management
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-elegant">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-wle-blue flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Fabrication Specialists</h3>
                <p className="text-sm text-muted-foreground">
                  Skilled craftsmen with decades of experience in stainless steel fabrication
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-elegant">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-wle-red flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Quality Assurance</h3>
                <p className="text-sm text-muted-foreground">
                  Certified quality specialists ensuring every tank meets stringent industry standards
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-foreground text-background">
        <div className="container text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Partner with Malaysia's Tank Experts
          </h2>
          <p className="text-lg mb-8 text-background/80 max-w-2xl mx-auto">
            Ready to discuss your next project? Our team is here to provide expert guidance 
            and custom solutions for your tank fabrication needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button asChild size="lg" variant="secondary">
              <Link to="/contact">Start Your Project</Link>
            </Button>
            <Button asChild size="lg" variant="secondary" className="bg-background/20 text-background hover:bg-background/30">
              <Link to="/portfolio">View Our Work</Link>
            </Button>
          </div>
          
          {/* Social Media Links */}
          <div className="flex justify-center gap-4">
            <Button asChild variant="secondary" size="sm" className="bg-background/20 text-background hover:bg-background/30">
              <a 
                href="https://www.facebook.com/WLEtech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Facebook className="h-4 w-4" />
                Follow Us
              </a>
            </Button>
            <Button asChild variant="secondary" size="sm" className="bg-background/20 text-background hover:bg-background/30">
              <a 
                href="https://www.linkedin.com/in/edwin-wong-wle/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Linkedin className="h-4 w-4" />
                Connect with Director
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;